import { useSearchParams } from 'react-router-dom';

import type { SidebarConfig } from '@/config/sidebarConfig';
import { adminSidebar } from '@/config/sidebarConfig';
import { listFeedbacksApi, updateFeedbackStatusApi } from '@/lib/api/feedbackApi';
import { useAuthStore } from '@/stores/auth-store';
import { formatDateAbbreviatedFr } from '@shared';
import { FeedbackStatus, FeedbackType } from '@shared/enums/domain.enum';
import type { Feedback } from '@shared/types/entities';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Bug, Inbox, Lightbulb, MessageSquare, Star } from 'lucide-react';
import { toast } from 'sonner';

import DashboardLayout from '../../components/layout/DashboardLayout';
import PageHeader from '../../components/ui/PageHeader';
import QueryErrorState from '../../components/ui/QueryErrorState';
import StatCard from '../../components/ui/StatCard';

const TYPE_CONFIG: Record<FeedbackType, { label: string; icon: typeof Bug; bg: string; text: string }> = {
  BUG: { label: 'Bug', icon: Bug, bg: 'bg-red-100', text: 'text-red-700' },
  AVIS: { label: 'Avis', icon: Star, bg: 'bg-amber-100', text: 'text-amber-700' },
  SUGGESTION: { label: 'Suggestion', icon: Lightbulb, bg: 'bg-blue-100', text: 'text-blue-700' },
};

const STATUS_CONFIG: Record<FeedbackStatus, { label: string; bg: string; text: string }> = {
  NEW: { label: 'Nouveau', bg: 'bg-emerald-100', text: 'text-emerald-700' },
  IN_REVIEW: { label: 'En cours', bg: 'bg-amber-100', text: 'text-amber-700' },
  RESOLVED: { label: 'Résolu', bg: 'bg-slate-100', text: 'text-slate-600' },
  ARCHIVED: { label: 'Archivé', bg: 'bg-slate-100', text: 'text-slate-400' },
};

const STATUS_ORDER: FeedbackStatus[] = [
  FeedbackStatus.NEW,
  FeedbackStatus.IN_REVIEW,
  FeedbackStatus.RESOLVED,
  FeedbackStatus.ARCHIVED,
];

function fullName(u?: Feedback['user']): string {
  if (!u) return '—';
  const n = `${u.firstName ?? ''} ${u.lastName ?? ''}`.trim();
  return n || u.email;
}

export default function AdminFeedbackList({ sidebar = adminSidebar }: { sidebar?: SidebarConfig } = {}) {
  const [searchParams, setSearchParams] = useSearchParams();
  const isAuthenticated = useAuthStore(s => s.isAuthenticated);
  const qc = useQueryClient();

  const statusFilter = (searchParams.get('status') ?? '') as FeedbackStatus | '';
  const typeFilter = (searchParams.get('type') ?? '') as FeedbackType | '';

  const { data, isLoading, isError, refetch } = useQuery({
    queryKey: ['admin', 'feedbacks', { status: statusFilter, type: typeFilter }],
    queryFn: () =>
      listFeedbacksApi({
        ...(statusFilter ? { status: statusFilter } : {}),
        ...(typeFilter ? { type: typeFilter } : {}),
      }),
    enabled: isAuthenticated,
  });

  const statusMutation = useMutation({
    mutationFn: ({ id, status }: { id: number; status: FeedbackStatus }) => updateFeedbackStatusApi(id, status),
    onSuccess: () => {
      toast.success('Statut mis à jour');
      qc.invalidateQueries({ queryKey: ['admin', 'feedbacks'] });
    },
    onError: () => toast.error('Erreur lors de la mise à jour'),
  });

  const rows = data?.data ?? [];
  const total = data?.meta.total ?? 0;
  const newCount = data?.meta.newCount ?? 0;
  const bugCount = rows.filter(r => r.type === 'BUG').length;

  function updateFilter(key: 'status' | 'type', value: string) {
    const next = new URLSearchParams(searchParams);
    if (value) next.set(key, value);
    else next.delete(key);
    setSearchParams(next, { replace: true });
  }

  return (
    <DashboardLayout sidebarConfig={sidebar}>
      <div className='p-8 space-y-6'>
        <PageHeader title='Retours utilisateurs' subtitle='Feedbacks envoyés via le widget (front & back-office)' />

        <div className='grid grid-cols-1 md:grid-cols-3 gap-5'>
          <StatCard
            icon={Inbox}
            iconBg='bg-emerald-100'
            iconColor='text-emerald-600'
            label='Nouveaux'
            value={String(newCount)}
            subtitle='À traiter'
          />
          <StatCard
            icon={MessageSquare}
            iconBg='bg-blue-100'
            iconColor='text-blue-600'
            label='Total (vue actuelle)'
            value={String(total)}
          />
          <StatCard
            icon={Bug}
            iconBg='bg-red-100'
            iconColor='text-red-600'
            label='Bugs (vue actuelle)'
            value={String(bugCount)}
          />
        </div>

        {isError && <QueryErrorState onRetry={() => void refetch()} />}

        <div className='bg-white rounded-2xl border border-slate-200'>
          <div className='flex items-center justify-between flex-wrap gap-3 px-6 py-4 border-b border-slate-200'>
            <div className='flex items-center gap-2'>
              <h2 className='text-lg font-bold text-slate-900'>Feedbacks</h2>
              {total > 0 && (
                <span className='px-2 py-0.5 bg-slate-100 text-slate-600 text-xs font-semibold rounded-full'>
                  {total}
                </span>
              )}
            </div>
            <div className='flex items-center gap-2'>
              <select
                value={typeFilter}
                onChange={e => updateFilter('type', e.target.value)}
                className='px-3 py-2 border border-slate-200 rounded-lg text-sm bg-white'
              >
                <option value=''>Tous les types</option>
                <option value='BUG'>Bug</option>
                <option value='AVIS'>Avis</option>
                <option value='SUGGESTION'>Suggestion</option>
              </select>
              <select
                value={statusFilter}
                onChange={e => updateFilter('status', e.target.value)}
                className='px-3 py-2 border border-slate-200 rounded-lg text-sm bg-white'
              >
                <option value=''>Tous les statuts</option>
                <option value='NEW'>Nouveau</option>
                <option value='IN_REVIEW'>En cours</option>
                <option value='RESOLVED'>Résolu</option>
                <option value='ARCHIVED'>Archivé</option>
              </select>
            </div>
          </div>

          {isLoading ? (
            <div className='p-6 space-y-3'>
              {Array.from({ length: 5 }).map((_, i) => (
                <div key={i} className='h-16 animate-pulse rounded-xl bg-slate-100' />
              ))}
            </div>
          ) : rows.length === 0 ? (
            <div className='flex flex-col items-center justify-center py-16 text-slate-400 gap-3'>
              <Inbox className='w-10 h-10 opacity-30' />
              <p className='text-sm font-medium'>Aucun feedback</p>
            </div>
          ) : (
            <div className='divide-y divide-slate-100'>
              {rows.map(r => {
                const typeCfg = TYPE_CONFIG[r.type];
                const statusCfg = STATUS_CONFIG[r.status];
                const TypeIcon = typeCfg.icon;
                const busy = statusMutation.isPending && statusMutation.variables?.id === r.id;
                return (
                  <div key={r.id} className='px-6 py-4 hover:bg-slate-50/60 transition'>
                    <div className='flex items-start justify-between gap-6 flex-wrap'>
                      <div className='flex-1 min-w-[300px]'>
                        <div className='flex items-center gap-3 mb-1.5 flex-wrap'>
                          <span
                            className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-semibold ${typeCfg.bg} ${typeCfg.text}`}
                          >
                            <TypeIcon className='w-3.5 h-3.5' />
                            {typeCfg.label}
                          </span>
                          <span
                            className={`px-2 py-0.5 rounded-full text-xs font-semibold ${statusCfg.bg} ${statusCfg.text}`}
                          >
                            {statusCfg.label}
                          </span>
                          <span className='text-xs text-slate-400'>{formatDateAbbreviatedFr(r.createdAt)}</span>
                          <span className='text-xs font-mono text-slate-400'>
                            {r.source === 'ADMIN' ? 'Back-office' : 'Site public'}
                          </span>
                        </div>
                        <p className='text-xs text-slate-500 mb-2'>
                          {fullName(r.user)} {r.user?.role ? `(${r.user.role})` : ''}
                        </p>
                        <p className='text-sm text-slate-700 bg-slate-50 rounded-lg px-3 py-2 whitespace-pre-wrap'>
                          {r.message}
                        </p>
                        {r.pageUrl && (
                          <a
                            href={r.pageUrl}
                            target='_blank'
                            rel='noreferrer'
                            className='mt-2 inline-block text-xs font-semibold text-blue-600 hover:underline break-all'
                          >
                            {r.pageUrl}
                          </a>
                        )}
                      </div>

                      <div className='flex items-center gap-2'>
                        <select
                          value={r.status}
                          disabled={busy}
                          onChange={e => statusMutation.mutate({ id: r.id, status: e.target.value as FeedbackStatus })}
                          className='px-3 py-2 border border-slate-200 rounded-lg text-sm bg-white disabled:opacity-50'
                        >
                          {STATUS_ORDER.map(s => (
                            <option key={s} value={s}>
                              {STATUS_CONFIG[s].label}
                            </option>
                          ))}
                        </select>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </div>
    </DashboardLayout>
  );
}
